/* eslint max-lines: ["error", { max: 435 }] -- this overrides for the entire file */
import type { LocaleDefinition } from "@faker-js/faker";
import { allLocales, faker } from "@faker-js/faker";
import { format } from "date-fns";

import { merge } from "@/__mocks__/mockHelpers";
import {
  fakeAdminEpisodeStatus,
  fakeUniversalStatusCode,
} from "@/__mocks__/user";
import { EnvironmentTypes } from "@/app.environments.js";
import { AppointmentTypeGroup, ProgramName } from "@/constants/Appointment";
import { DateFormat } from "@/constants/DateFormat";
import { LaunchDarklyConnectionMode } from "@/crossPlatform/LaunchDarkly/config";
import type { MinimalPatientDocument } from "@/hooks/useTasksFileUpload";
import type { AppUserSliceState } from "@/store/reducers/appUser";
import { initialState } from "@/store/reducers/appUser";
import type { AvatarCardProps } from "@/ui/core";
import { IllustrationName } from "@/ui/theme/icons/illustrations/groupClasses/IllustrationDefinitions";
import { getInitialsFromName } from "@/utils/user.util";

const fakeAccountStatus = (): string =>
  faker.helpers.arrayElement(["active", "inactive"]);

const fakeBaseUserType = (): BaseUserType =>
  faker.helpers.arrayElement(["patient", "provider", "support"]);

const fakeError = (): Error => new Error(faker.word.words());

const fakeSendbirdUserType = (): SendbirdUserType =>
  faker.helpers.arrayElement(["Patient", "Provider", "Support"]);

const fakeAttendeeTypes = (): AttendeeType[] =>
  faker.helpers.arrayElements(["Patients", "Supports"], { max: 2, min: 1 });

const ProviderTypes = {
  AdmissionsSpecialist: "Admissions Specialist",
  Analyst: "Analyst",
  CareCoordinator: "Care Coordinator",
  CaregiverCoach: "Caregiver Coach",
  CareNavigator: "Care Navigator",
  Dietitian: "Dietitian",
  FamilyMentor: "Family Mentor",
  MedicalAssistant: "Medical Assistant",
  NursePractitioner: "Nurse Practitioner",
  PeerMentor: "Peer Mentor",
  Physician: "Physician",
  Psychiatrist: "Psychiatrist",
  RecoveryCoach: "Recovery Coach",
  RecoveryNavigator: "Recovery Navigator",
  Therapist: "Therapist",
};

const fakeProviderType = (): string =>
  faker.helpers.arrayElement([
    ProviderTypes.Dietitian,
    ProviderTypes.PeerMentor,
    ProviderTypes.Physician,
    ProviderTypes.Therapist,
    ProviderTypes.AdmissionsSpecialist,
    ProviderTypes.Psychiatrist,
    ProviderTypes.FamilyMentor,
    ProviderTypes.NursePractitioner,
    ProviderTypes.RecoveryNavigator,
    ProviderTypes.MedicalAssistant,
    ProviderTypes.Analyst,
    ProviderTypes.CareCoordinator,
  ]);

const fakeEventRecurrence = (): EventRecurrence =>
  faker.helpers.arrayElement([
    "Biweekly",
    "Does not repeat",
    "Monthly",
    "Weekly",
  ]);

const fakeAppointmentTypeGroup = (): AppointmentTypeGroup =>
  faker.helpers.arrayElement(Object.values(AppointmentTypeGroup));

const fakeIllustrationName = (): IllustrationName =>
  faker.helpers.arrayElement(Object.values(IllustrationName));

const fakePronouns = ({
  max = 3,
  min = 1,
}: {
  max?: number;
  min?: number;
}): string[] =>
  faker.helpers
    .multiple(
      () =>
        faker.helpers.arrayElement([
          "He/Him/His",
          "She/Her/Hers",
          "They/Them/Theirs",
          "Other",
          "Ey/Em/Eirs",
          "Xe/Xem/Xir",
          "(F)ae/(F)aer/(F)aers",
        ]),
      {
        count: faker.number.int({ max, min }),
      },
    )
    .flat();

const fakeContactPreferences = (): ContactPreference[] =>
  faker.helpers.arrayElements(
    ["Phone", "Email", "No preference", "SMS", "Unknown"],
    { max: 5, min: 1 },
  );

const fakeLocale = (): LocaleDefinition => {
  // faker-exported allLocales missing extra type information
  const locales: { [key: string]: LocaleDefinition } = allLocales;
  return locales[faker.helpers.arrayElement(Object.keys(locales))];
};

const fakeLanguage = (): string => fakeLocale().metadata?.title || "English";

const fakeLanguages = (): string[] =>
  faker.helpers.multiple(() => fakeLanguage(), {
    count: faker.number.int({ max: 3, min: 1 }),
  });

const fakeBaseUserAvatarProps = (
  overrides?: DeepPartial<BaseUserAvatarProps>,
): BaseUserAvatarProps => {
  const userAvatar: BaseUserAvatarProps = {
    imageUrl: faker.internet.url(),
    userId: faker.string.uuid(),
    userName: faker.person.fullName(),
    userType: fakeBaseUserType(),
  };

  return merge(userAvatar, overrides);
};

const fakeAvatarCardProps = (
  overrides?: DeepPartial<AvatarCardProps>,
): AvatarCardProps => {
  const avatarCard: AvatarCardProps = {
    ...fakeBaseUserAvatarProps(overrides),
    pronouns: fakePronouns({ max: 2, min: 0 }),
  };
  return merge(avatarCard, overrides);
};

const fakeBaseUserAvatarPropsWithUniqueInitials = ({
  max = 10,
  min = 2,
}: {
  max?: number;
  min?: number;
}): BaseUserAvatarProps[] => {
  const userInitials: string[] = [];
  return faker.helpers.multiple(
    () => {
      // for unit tests that check the screen for user initials,
      // ensure that every user has UNIQUE initials to prevent flaky tests
      while (true) {
        const props = fakeBaseUserAvatarProps();
        const initials = getInitialsFromName(props.userName);
        if (userInitials.includes(initials)) continue;
        userInitials.push(initials);
        return props;
      }
    },
    {
      count: { max, min },
    },
  );
};

const fakeBookingAttendee = (
  overrides?: DeepPartial<BookingAttendee>,
): BookingAttendee => {
  const attendee: BookingAttendee = {
    chosenName: faker.person.middleName(),
    externalId: faker.string.uuid(),
    firstName: faker.person.firstName(),
    isRequired: faker.datatype.boolean(),
    lastName: faker.person.lastName(),
    userType: fakeBaseUserType(),
  };

  return merge(attendee, overrides);
};

const fakeAppointmentType = (
  overrides?: DeepPartial<AppointmentType>,
): AppointmentType => {
  const appointmentType: AppointmentType = {
    appointmentType: faker.lorem.sentence(),
    appointmentTypeExtId: faker.string.uuid(),
    appointmentTypeGroup: faker.lorem.word(),
    appointmentTypeLabel: faker.lorem.words(2),
    bufferInMinutes: faker.number.int({ max: 30, min: 0 }),
    durationInMinutes: faker.number.int({ max: 120, min: 15 }),
    endTime: format(faker.date.future(), DateFormat.time),
    infoText: faker.lorem.sentence(),
    startTime: format(faker.date.future(), DateFormat.time),
  };

  return merge(appointmentType, overrides);
};

const fakeBookingProviderType = (
  overrides?: DeepPartial<BookingProviderType>,
): BookingProviderType => {
  const providerType: BookingProviderType = {
    providerExternalId: faker.string.uuid(),
    providerName: faker.person.fullName(),
    providerType: fakeProviderType(),
  };

  if (faker.datatype.boolean()) {
    providerType.infoText = faker.lorem.sentence();
  }

  return merge(providerType, overrides);
};

const fakeGooglePlacesAddress = (
  overrides?: DeepPartial<GooglePlacesAddress>,
) => {
  const address: GooglePlacesAddress = {
    addressLine1: faker.location.streetAddress(),
    addressLine2: faker.location.secondaryAddress(),
    affiliationName: faker.lorem.sentence(),
    city: faker.location.city(),
    country: faker.location.country(),
    county: faker.location.county(),
    latitude: faker.location.latitude(),
    longitude: faker.location.longitude(),
    name: faker.lorem.sentence(),
    state: faker.location.state(),
    timezone: faker.location.timeZone(),
    zip: faker.location.zipCode(),
    zipCode: faker.location.zipCode(),
  };
  return merge(address, overrides);
};

const fakeTreatmentEpisodeProgram = (
  overrides?: DeepPartial<TreatmentEpisodeProgram>,
): DeepRequired<TreatmentEpisodeProgram> => {
  const episode: DeepRequired<TreatmentEpisodeProgram> = {
    endDate: faker.date.past().toISOString(),
    programName: faker.lorem.sentence(),
    startDate: faker.date.past().toISOString(),
    treatmentEpisodeProgramId: faker.string.uuid(),
    treatmentProgramClient: {
      code: faker.word.words(),
      isDeleted: faker.datatype.boolean(),
      label: faker.word.words(),
    },
    treatmentProgramCode: faker.word.words(),
  };
  return merge(episode, overrides);
};

const fakeLinkedPatientProfile = (
  overrides?: DeepPartial<LinkedPatientProfile>,
): DeepRequired<LinkedPatientProfile> => {
  // eslint-disable-next-line no-restricted-syntax -- LinkedPatientProfile has a lot of properties
  const profile: DeepRequired<LinkedPatientProfile> = {
    address: fakeGooglePlacesAddress(),
    adminEpisodeStatus: fakeAdminEpisodeStatus(),
    admitDate: format(faker.date.past(), DateFormat.yearMonthDay),
    cellPhone: faker.phone.number(),
    chosenName: faker.person.firstName(),
    contactPreferences: fakeContactPreferences(),
    dateOfBirth: format(faker.date.birthdate(), DateFormat.yearMonthDay),
    email: faker.internet.email(),
    externalId: faker.string.uuid(),
    firstName: faker.person.firstName(),
    gender: faker.person.gender(),
    hasGivenRecordingConsent: faker.datatype.boolean(),
    isPatient: faker.datatype.boolean(),
    isProxy: faker.datatype.boolean(),
    languages: fakeLanguages(),
    lastName: faker.person.lastName(),
    preferences: {
      notifications: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        deviceCalendarEnabled: faker.datatype.boolean(),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        googleCalendarEnabled: faker.datatype.boolean(),
        // eslint-disable-next-line @typescript-eslint/naming-convention -- matches API field name
        loggedInToApp: faker.datatype.boolean(),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        receiveEmails: faker.datatype.boolean(),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        receivePush: faker.datatype.boolean(),
        // eslint-disable-next-line @typescript-eslint/naming-convention
        receiveSms: faker.datatype.boolean(),
        smsConsentStatus: "OPTED_IN",
      },
    },
    programName: faker.lorem.sentence(),
    pronouns: fakePronouns({}),
    relationToPatient: faker.word.words(),
    targetTreatmentProgram: faker.helpers.arrayElement(
      Object.values(ProgramName),
    ),
    timezone: faker.location.timeZone(),
    treatmentEpisodePrograms: faker.helpers.multiple(
      () => fakeTreatmentEpisodeProgram(),
      { count: { max: 10, min: 3 } },
    ),
    universalStatusCode: fakeUniversalStatusCode(),
  };
  return merge(profile, overrides);
};

const fakeLinkedPatientProfiles = (): DeepRequired<LinkedPatientProfile>[] =>
  faker.helpers.multiple(() => fakeLinkedPatientProfile(), {
    count: { max: 10, min: 0 },
  });

const fakeSupportProfile = (
  overrides?: DeepPartial<SupportProfile>,
): DeepRequired<SupportProfile> => {
  const {
    chosenName,
    externalId,
    firstName,
    isProxy,
    lastName,
    relationToPatient,
  } = fakeLinkedPatientProfile();
  const supportProfile: DeepRequired<SupportProfile> = {
    chosenName,
    externalId,
    firstName,
    isProxy,
    lastName,
    relationToPatient,
  };
  return merge(supportProfile, overrides);
};

const fakeFullProfileResponse = (
  overrides?: DeepPartial<FullProfileResponse>,
): DeepRequired<FullProfileResponse> => {
  const profile: DeepRequired<FullProfileResponse> = {
    ...fakeLinkedPatientProfile(),
    linkedPatients: fakeLinkedPatientProfiles(),
    supportedBy: faker.helpers.multiple(() => fakeSupportProfile(), {
      count: { max: 10, min: 0 },
    }),
  };
  return merge(profile, overrides);
};

const fakeAppUser = (
  overrides?: DeepPartial<AppUserSliceState>,
): AppUserSliceState => {
  const isPatient = overrides?.isPatient ?? faker.datatype.boolean();
  const appUser: AppUserSliceState = {
    ...initialState,
    externalId: faker.string.uuid(),
    firstName: faker.person.firstName(),
    hasGivenRecordingConsent: faker.datatype.boolean(),
    isPatient,
    isSupport: !isPatient,
    lastName: faker.person.lastName(),
  };
  return merge(appUser, overrides);
};

const fakeMinimalPatientDocument = (
  overrides?: DeepPartial<MinimalPatientDocument>,
): MinimalPatientDocument => {
  const document: MinimalPatientDocument = {
    documentExternalId: faker.string.uuid(),
    patientExternalId: faker.string.uuid(),
  };
  return merge(document, overrides);
};

const fakeMinimalPatientDocuments = (): MinimalPatientDocument[] =>
  faker.helpers.multiple(() => fakeMinimalPatientDocument(), {
    count: { max: 10, min: 1 },
  });

const fakeDeploymentEnvironment = (): string =>
  faker.helpers.arrayElement(Object.values(EnvironmentTypes));

const fakeLaunchDarklyConnectionMode = (): string =>
  faker.helpers.arrayElement(Object.values(LaunchDarklyConnectionMode));

export {
  fakeAccountStatus,
  fakeAppointmentType,
  fakeAppointmentTypeGroup,
  fakeAppUser,
  fakeAttendeeTypes,
  fakeAvatarCardProps,
  fakeBaseUserAvatarProps,
  fakeBaseUserAvatarPropsWithUniqueInitials,
  fakeBaseUserType,
  fakeBookingAttendee,
  fakeBookingProviderType,
  fakeContactPreferences,
  fakeDeploymentEnvironment,
  fakeError,
  fakeEventRecurrence,
  fakeFullProfileResponse,
  fakeGooglePlacesAddress,
  fakeIllustrationName,
  fakeLanguages,
  fakeLaunchDarklyConnectionMode,
  fakeLinkedPatientProfile,
  fakeLinkedPatientProfiles,
  fakeMinimalPatientDocuments,
  fakePronouns,
  fakeProviderType,
  fakeSendbirdUserType,
  fakeTreatmentEpisodeProgram,
  ProviderTypes,
};
